import React, { useMemo } from 'react';
import T from 'prop-types';

import * as Classes from './styles.module.scss';
import Mount from './mount';
import MatColor from './mat_color';

export const FULL_BLEED = 'fullBleed';
export const MATTE = 'matte';
export const FLOAT_DECKLED = 'dckl';
export const FLOAT_STRIGHT = 'fltn';
export const FLOATING = 'floating';

export const MAT_COLORS_ORDER = ['White', 'Deep Red', 'Indigo', 'Moss Point Green'];

export const mat = () => (window.currentLocale.currentLocale === 'en' ? '2.75"' : '7cm');
const matteText = (caps) => (caps ? 'M' : 'm') + (window.currentLocale.currentLocale === 'en' ? 'atte' : 'ount');

const VALID_MOUNT_KINDS = [MATTE, FULL_BLEED, FLOAT_STRIGHT, FLOAT_DECKLED];

const Mounts = ({
  onSelect, selectedMount, selectedMatColor, paperId, matte,
  fullBleed, dckl, fltn, matColors, matColorsRgb
}) => {
  const onChange = (e) => {
    const { value } = e.target;
    if (value === FLOATING) {
      onSelect(FLOAT_STRIGHT, null);
    } else if (value === MATTE) {
      onSelect(MATTE, selectedMatColor);
    } else if (value.indexOf(';') > -1) {
      const [kind, color] = value.split(';');
      onSelect(kind, color);
    } else {
      onSelect(value, selectedMatColor);
    }
  };

  const renderMatte = useMemo(() => {
    const description = selectedMatColor
      ? ` - ${ mat() } wide, ${ selectedMatColor } matboard, buffered for extended life`
      : ` - ${ mat() } wide matboard, buffered for extended life`;
    if (matte) {
      return (
        <Mount
          kind={ MATTE }
          name={ `With ${ matteText(true) }` }
          description={ description }
          onSelect={ onChange }
          paperId={ paperId }
          isSelected={ selectedMount === MATTE }
        >
          <div className={ Classes.matColors }>
            { MAT_COLORS_ORDER.map((color) => (
              (matColors.includes(color) ? (
                <MatColor
                  name={ `${ color } ${ matteText(true) }` }
                  kind={ `${ MATTE };${ color }` }
                  key={ color }
                  onSelect={ onChange }
                  paperId={ paperId }
                  isSelected={ selectedMount === MATTE && selectedMatColor === color }
                  postFix={ color }
                  colorRgb={ matColorsRgb[matColors.indexOf(color)] }
                />
              ) : null)
            )) }
          </div>
        </Mount>
      );
    }
    return null;
  }, [matte, matColors, selectedMatColor, selectedMount]);

  const renderFillBleed = useMemo(() => {
    if (fullBleed) {
      return (
        <Mount
          kind={ FULL_BLEED }
          name={ `No ${ matteText(true) }` }
          description={ ` - no ${ matteText(false) } or border, image is flush to frame` }
          onSelect={ onChange }
          paperId={ paperId }
          isSelected={ selectedMount === FULL_BLEED }
        />
      );
    }
    return null;
  }, [fullBleed, selectedMount, matColors, selectedMatColor]);

  const renderFloating = useMemo(() => {
    if (dckl || fltn) {
      return (
        <Mount
          kind={ FLOATING }
          name="Float Mount"
          description="- image floated on 1.25&quot; wide white matte"
          onSelect={ onChange }
          paperId={ paperId }
          isSelected={ selectedMount === FLOAT_STRIGHT || selectedMount === FLOAT_DECKLED }
        >
          <div className={ Classes.floatForm }>
            { fltn && (
              <Mount
                kind={ FLOAT_STRIGHT }
                name="Straight Edge"
                description=" - straight paper edges"
                onSelect={ onChange }
                paperId={ paperId }
                isSelected={ selectedMount === FLOAT_STRIGHT }
                postFix="float"
              />
            ) }
            { dckl && (
              <Mount
                kind={ FLOAT_DECKLED }
                name="Deckled Edge"
                description=" - rough paper edges"
                onSelect={ onChange }
                paperId={ paperId }
                isSelected={ selectedMount === FLOAT_DECKLED }
                postFix="float"
              />
            ) }
          </div>
        </Mount>
      );
    }
    return null;
  }, [dckl, fltn, selectedMount, matColors, selectedMatColor]);

  return (
    <form className={ Classes.mounts }>
      { renderMatte }
      { renderFillBleed }
      { renderFloating }
    </form>
  );
};

Mounts.propTypes = {
  onSelect: T.func.isRequired,
  selectedMount: T.oneOf(VALID_MOUNT_KINDS),
  selectedMatColor: T.string,
  paperId: T.oneOfType([T.number, T.string]).isRequired,
  matte: T.bool,
  fullBleed: T.bool,
  dckl: T.bool,
  fltn: T.bool,
  matColors: T.arrayOf(T.string),
  matColorsRgb: T.arrayOf(T.arrayOf(T.number))
};

export default Mounts;
